/**
 * GameEngine - Core game logic service
 * Handles game initialization, state transitions, and game rules
 */

import { GameState, GameMode } from '../types/GameTypes';
import { GAME_CONFIG } from '../config';

// Forward declaration for state manager
export interface IStateManager {
  saveGameState?(gameState: GameState, gameMode: GameMode): void;
  handleDailyGameCompletion?(gameState: GameState): void;
  loadGameState?(gameMode: GameMode): GameState | null;
}

export class GameEngine {
  
  /**
   * Start a new game with word selection and initial letter reveal
   * This replaces the scattered initialization logic from the original
   * Returns complete game state ready for components
   */
  static startNewGame(
    word: string, 
    rstlneMode: boolean, 
    stateManager?: IStateManager, 
    gameMode: GameMode = 'free-play'
  ): GameState {
    
    // Validate critical inputs (throw for unrecoverable errors)
    if (!word || word.length === 0) {
      throw new Error('Cannot start game: no word provided');
    }

    // For daily mode, check if we have existing state for this word
    if (gameMode === 'daily' && stateManager?.loadGameState) {
      const existingState = stateManager.loadGameState(gameMode);
      if (existingState && existingState.secretWord === word.toUpperCase()) {
        // Return existing state if it matches the word
        return {
          ...existingState,
          message: existingState.message || 'Continuing daily game...',
          lastAction: 'game_restored'
        };
      }
    }

    // Initialize base game state
    const gameState: GameState = {
      secretWord: word.toUpperCase(),
      revealedIndices: [],
      guessedLetters: [],
      autoRevealedLetters: [],
      letterGuessesLeft: GAME_CONFIG.maxLetterGuesses,
      gameOver: false,
      gameWon: false,
      gameLost: false,
      score: 0,
      message: '',
      lastAction: 'game_started'
    };

    // Reveal initial letters based on mode
    if (rstlneMode) {
      // RSTLNE mode: Reveal all R,S,T,L,N,E instances
      const revealedLettersInWord: string[] = [];
      
      for (let i = 0; i < word.length; i++) {
        const letter = word[i].toUpperCase();
        if (GAME_CONFIG.rstlneLetters.includes(letter) && !gameState.revealedIndices.includes(i)) {
          gameState.revealedIndices.push(i);
          if (!revealedLettersInWord.includes(letter)) {
            revealedLettersInWord.push(letter);
          }
        }
      }
      
      // In RSTLNE mode, ALL RSTLNE letters are marked as "guessed"
      gameState.autoRevealedLetters = [...GAME_CONFIG.rstlneLetters];
      gameState.guessedLetters = [...GAME_CONFIG.rstlneLetters];
      
      // No message needed for RSTLNE letter reveals
    } else {
      // Standard mode: Reveal one random letter and all its instances
      const randomIndex = Math.floor(Math.random() * word.length);
      const letterToReveal = word[randomIndex].toUpperCase();
      
      for (let i = 0; i < word.length; i++) {
        if (word[i].toUpperCase() === letterToReveal) {
          gameState.revealedIndices.push(i);
        }
      }
      
      gameState.autoRevealedLetters = [letterToReveal];
      gameState.guessedLetters = [letterToReveal];
      gameState.message = `Revealed the letter "${letterToReveal}" to start the game.`;
    }

    // Save initial state if state manager provided
    if (stateManager?.saveGameState) {
      stateManager.saveGameState(gameState, gameMode);
    }

    return gameState;
  }

  /**
   * Process a letter guess and return updated game state
   * Handles all validation and state updates in one clean function
   */
  static makeLetterGuess(
    currentState: GameState, 
    letter: string, 
    stateManager?: IStateManager,
    gameMode: GameMode = 'free-play'
  ): GameState {
    
    // Clean the input
    const cleanLetter = letter.trim().toUpperCase();
    
    // Validate input - return current state with message for recoverable issues
    if (!/^[A-Z]$/.test(cleanLetter)) {
      return {
        ...currentState,
        message: 'Please select a valid letter.',
        lastAction: 'invalid_input'
      };
    }

    // Game over check
    if (currentState.gameOver) {
      return {
        ...currentState,
        message: 'Game is over. Start a new game to play again.',
        lastAction: 'game_over_attempt'
      };
    }

    // Already guessed check
    if (currentState.guessedLetters.includes(cleanLetter)) {
      return {
        ...currentState,
        message: `You already guessed the letter "${cleanLetter}".`,
        lastAction: 'duplicate_guess'
      };
    }

    // No guesses left check
    if (currentState.letterGuessesLeft <= 0) {
      return {
        ...currentState,
        message: 'No letter guesses remaining.',
        lastAction: 'no_guesses_left'
      };
    }

    // Create new state for the guess
    const newState = { ...currentState };
    newState.guessedLetters = [...currentState.guessedLetters, cleanLetter];
    newState.lastAction = 'letter_guessed';

    // Check if letter is in the word
    if (currentState.secretWord.includes(cleanLetter)) {
      // Letter is correct - reveal all instances
      const newRevealedIndices = [...currentState.revealedIndices];
      let foundNewInstances = false;

      for (let i = 0; i < currentState.secretWord.length; i++) {
        if (currentState.secretWord[i] === cleanLetter && !newRevealedIndices.includes(i)) {
          newRevealedIndices.push(i);
          foundNewInstances = true;
        }
      }

      if (foundNewInstances) {
        newState.revealedIndices = newRevealedIndices;
        newState.message = `Good guess! The letter "${cleanLetter}" is in the word.`;
        
        // Check for win condition - all letters revealed
        if (newRevealedIndices.length === currentState.secretWord.length) {
          newState.score = currentState.letterGuessesLeft; // Score is remaining guesses
          newState.gameOver = true;
          newState.gameWon = true;
          newState.message = `Congratulations! You've revealed all letters in "${currentState.secretWord}"! Your score: ${newState.score}`;
          newState.lastAction = 'game_won';
          
          // Handle game completion
          if (gameMode === 'daily' && stateManager?.handleDailyGameCompletion) {
            stateManager.handleDailyGameCompletion(newState);
          }
        }
      } else {
        // Letter already revealed
        newState.message = `You already revealed all instances of "${cleanLetter}" in the word.`;
        newState.lastAction = 'already_revealed';
      }
    } else {
      // Letter is incorrect - consume a guess
      newState.letterGuessesLeft = currentState.letterGuessesLeft - 1;
      newState.message = `The letter "${cleanLetter}" is not in the word. ${newState.letterGuessesLeft} letter guesses left.`;
      
      // Check for loss condition - no guesses left
      if (newState.letterGuessesLeft === 0) {
        newState.gameOver = true;
        newState.gameLost = true;
        newState.score = 0;
        newState.message = `Game over! You've used all ${GAME_CONFIG.maxLetterGuesses} letter guesses without revealing the word. The word was "${currentState.secretWord}".`;
        newState.lastAction = 'game_lost';
        
        // Handle game completion
        if (gameMode === 'daily' && stateManager?.handleDailyGameCompletion) {
          stateManager.handleDailyGameCompletion(newState);
        }
      }
    }

    // Save state if state manager provided
    if (stateManager?.saveGameState) {
      stateManager.saveGameState(newState, gameMode);
    }

    return newState;
  }

  /**
   * Check if the game is won
   */
  static isGameWon(state: GameState): boolean {
    return state.revealedIndices.length === state.secretWord.length;
  }

  /**
   * Check if the game is lost
   */
  static isGameLost(state: GameState): boolean {
    return state.letterGuessesLeft <= 0 && !this.isGameWon(state);
  }

  /**
   * Calculate final score
   */
  static calculateScore(state: GameState): number {
    if (this.isGameWon(state)) {
      return state.letterGuessesLeft;
    }
    return 0;
  }

  /**
   * Create a clean initial game state (for testing or manual setup)
   */
  static createInitialState(secretWord: string): GameState {
    return {
      secretWord: secretWord.toUpperCase(),
      revealedIndices: [],
      guessedLetters: [],
      autoRevealedLetters: [],
      letterGuessesLeft: GAME_CONFIG.maxLetterGuesses,
      gameOver: false,
      gameWon: false,
      gameLost: false,
      score: 0,
      message: 'Game ready to start.',
      lastAction: 'initialized'
    };
  }
}
